import { defineStore } from 'pinia'
import type {
  CategoryItem,
  CategoryTree,
  SearchFacet,
  SearchFilter,
  Product,
  SelectedFilterValue,
  StatsRange,
  SelectedFilter,
} from '@/types/searchTypes'
import { SortDirection, FacetType, FacetKeyEnum } from '@/types/searchTypes'
import getEmptyCategory from '~/utils/getEmptyCategory'
import type { Breadcrumb } from '~/types/Product'
import type { TTranslationOptions } from '~/composables/useLang'
import { SearchEnum } from '~/enums/SearchEnum'
import { FLAGS_FILTER_KEY } from '~/composables/constants/search'
import { ContentType, useStoreConfig } from './storeConfig'

export const defaultSortType = SortDirection.BestSellers

export interface IProductContentState {
  isBelowLimit?: boolean
  searchQuery: string
  products: Product[]
  similarResults: Product[]
  facets: SearchFacet[]
  selectedFilters?: Map<string, { value: string[] | number[]; type: FacetType }>
  temporarilySelectedFilters?: Map<string, { value: string[] | number[]; type: FacetType }>
  limit: number
  page: number
  total: number
  sortType: SortDirection
  category?: CategoryItem | CategoryTree
  categoryBreadcrumbs: Breadcrumb[]
  queryKey: string | undefined
  categoryParam: number | number[] | null
  prevCategoryParam: number | number[] | null
  disabledAdditionalIdsInIndex: number[]
  contentType: ContentType
}

function getInitialState(): IProductContentState {
  const router = useRouter()
  const type = router.currentRoute.value.query?.type
  const config = useStoreConfig()
  const locale = useState<string>('locale.setting')
  const sortType =
    config.getSite?.languages?.find((lang) => lang.iso_code.toLowerCase() === locale.value.toLowerCase())?.pivot
      .mp_default_sort ?? SortDirection.BestSellers

  return {
    isBelowLimit: false,
    searchQuery: '',
    products: [],
    facets: [],
    limit: SearchEnum.FirstPage,
    page: 1,
    total: 0,
    selectedFilters: undefined,
    temporarilySelectedFilters: undefined,
    category: undefined,
    sortType,
    categoryBreadcrumbs: [],
    queryKey: undefined,
    categoryParam: null,
    prevCategoryParam: null,
    disabledAdditionalIdsInIndex: [],
    similarResults: [],
    contentType: type === ContentType.Offers ? ContentType.Offers : ContentType.Products,
  }
}

function extractAdditionalFieldId(facet: SearchFacet): number | null {
  const regex = /^additional_field_(?<id>\d+)$/i

  const str = facet.key
  const m = regex.exec(str)

  let additionalFieldId: null | string = null

  if (m !== null && m.groups && m.groups.id) {
    additionalFieldId = m.groups.id
  }

  return additionalFieldId ? parseInt(additionalFieldId) : null
}

export const useProductContentStore = defineStore('productContentStore', {
  state: (): IProductContentState => getInitialState(),
  actions: {
    setPage(page: number) {
      this.page = page
    },
    setQueryKey(value: string | undefined) {
      this.queryKey = value
    },
    setProducts(products: Product[]) {
      this.products = products
    },
    setSimilarResults(value?: Product[]) {
      this.similarResults = value ?? []
    },
    appendProducts(products: Product[]) {
      this.products = this.products.concat(products)
    },
    setFacets(facets: SearchFacet[]) {
      const filteredFacets = facets.filter((facet) => {
        return (
          (Object.values(FacetKeyEnum) as string[]).includes(facet.key) || facet.key.startsWith('additional_field_')
        )
      })

      this.facets = filteredFacets.map((facet) => {
        if (facet.key === FacetKeyEnum.DISCOUNT_PERCENTAGE && facet.type === FacetType.Stats) {
          return { ...facet, min: 0, max: 100 }
        }

        return facet
      })
    },
    setLimit(limit: number) {
      this.limit = limit
    },
    setTotal(total: number) {
      this.total = total
    },
    setSortType(sort?: SortDirection) {
      if (sort) {
        this.sortType = sort
      }
    },
    setDisabledAdditionalIdsInIndex(ids: number[]) {
      this.disabledAdditionalIdsInIndex = ids
    },
    setContentData({
      products,
      facets,
      total,
      searchText,
      filters,
      searchFacets,
      sort,
      limit,
      page,
      queryKey,
      disabledAdditionalIdsInIndex,
      similarResults,
    }: {
      products: Product[]
      facets: SearchFacet[]
      total: number
      searchText?: string
      filters: SearchFilter
      searchFacets: SearchFacet[]
      sort: SortDirection | undefined
      categorySlug?: string
      limit: number
      page: number
      queryKey: string | undefined
      disabledAdditionalIdsInIndex: number[]
      similarResults?: Product[]
    }) {
      this.setSearchQuery(searchText)
      this.setProducts(products)
      this.setSelectedFilters(filters, searchFacets)
      this.setFacets(facets)
      this.setTotal(total)
      this.setSortType(sort)
      this.setLimit(limit)
      this.setPage(page)
      this.setQueryKey(queryKey)
      this.setDisabledAdditionalIdsInIndex(disabledAdditionalIdsInIndex)
      this.setSimilarResults(similarResults)
    },
    appendContentData({
      products,
      facets,
      total,
      searchText,
      filters,
      searchFacets,
      sort,
      limit,
      page,
      queryKey,
      disabledAdditionalIdsInIndex,
      similarResults,
    }: {
      products: Product[]
      facets: SearchFacet[]
      total: number
      searchText?: string
      filters: SearchFilter
      searchFacets: SearchFacet[]
      sort: SortDirection | undefined
      categorySlug?: string
      limit: number
      page: number
      queryKey: string | undefined
      disabledAdditionalIdsInIndex: number[]
      similarResults?: Product[]
    }) {
      this.setSearchQuery(searchText)
      this.appendProducts(products)
      this.setSelectedFilters(filters, searchFacets)
      this.setFacets(facets)
      this.setTotal(total)
      this.setSortType(sort)
      this.setLimit(limit)
      this.setPage(page)
      this.setQueryKey(queryKey)
      this.setDisabledAdditionalIdsInIndex(disabledAdditionalIdsInIndex)
      this.setSimilarResults(similarResults)
    },
    setSelectedFilters(filters: SearchFilter, searchFacets: SearchFacet[]) {
      const newFilters = new Map()

      Object.keys(filters).forEach((key) => {
        const filterType = searchFacets.find((facet) => facet.key === key)?.type

        if (filterType === FacetType.Stats) {
          const { gte, lte } = filters[key] as StatsRange

          if (isNaN(+gte) || isNaN(+lte)) {
            return
          }

          let statsRange = [+gte, +lte]

          if (key === FacetKeyEnum.DISCOUNT_PERCENTAGE) {
            statsRange = [Math.round((1 - +lte) * 100), Math.round((1 - +gte) * 100)]
          }

          return newFilters.set(key, {
            value: statsRange,
            type: FacetType.Stats,
          })
        }

        newFilters.set(key, {
          value: filters[key],
          type: key === FLAGS_FILTER_KEY ? FacetType.Flags : filterType,
        })
      })

      this.selectedFilters = new Map(newFilters)
      this.temporarilySelectedFilters = new Map(newFilters)
    },

    setTemporarilySelectedFilter(filter: { [key: string]: { value: SelectedFilterValue; type: FacetType } }) {
      const filterKey = Object.keys(filter)?.[0]

      if (filter[filterKey]?.type === FacetType.Terms) {
        const filterValue = filter[filterKey].value as string
        const currentFilterValues = this.temporarilySelectedFilters?.get(filterKey)?.value as string[] | undefined

        if (currentFilterValues) {
          // Remove if the same filter is found.
          if (currentFilterValues?.includes(filterValue)) {
            const newFilters = currentFilterValues?.filter((value) => value !== filterValue)

            if (newFilters.length) {
              return this.temporarilySelectedFilters?.set(filterKey, {
                value: newFilters,
                type: filter[filterKey].type,
              })
            } else {
              this.temporarilySelectedFilters?.delete(filterKey)
            }
            // Add if currently selected filter is not found.
          } else {
            this.temporarilySelectedFilters?.set(filterKey, {
              value: currentFilterValues.concat(filterValue),
              type: filter[filterKey].type,
            })
          }
          // Add if it's a new filter.
        } else {
          this.temporarilySelectedFilters?.set(filterKey, {
            type: filter[filterKey].type,
            value: [filterValue],
          })
        }
      }

      if (filter[filterKey]?.type === FacetType.Stats) {
        const filterValue = filter[filterKey].value as number[]

        // Passed as empty array if values aren't changed
        if (!filterValue.length) {
          return this.temporarilySelectedFilters?.delete(filterKey)
        }

        this.temporarilySelectedFilters?.set(filterKey, {
          type: filter[filterKey].type,
          value: filterValue,
        })
      }
    },
    setSearchQuery(query?: string) {
      this.searchQuery = query ?? ''
    },
    setCategory(
      category?: CategoryItem | CategoryTree,
      allCategories?: CategoryItem[],
      t?: (value: string, opts?: TTranslationOptions) => string
    ) {
      this.category = category ?? getEmptyCategory(t, this.contentType)

      this.categoryBreadcrumbs = this.findCategoriesForBreadcrumbs(category, allCategories, t)
    },
    setTemporarilySelectedFilters(filters: Map<string, SelectedFilter>) {
      this.temporarilySelectedFilters = filters
    },
    resetTemporarilySelectedFilters() {
      this.temporarilySelectedFilters = new Map(this.selectedFilters)
    },
    resetProductContentStore() {
      this.products = []
      this.facets = []
      this.limit = getInitialState().limit
      this.selectedFilters = undefined
      this.total = 0
      this.sortType = getInitialState().sortType
      this.category = undefined
      this.categoryBreadcrumbs = []
    },

    findCategoriesForBreadcrumbs(
      category?: CategoryItem,
      allCategories?: CategoryItem[],
      t?: (value: string, opts?: TTranslationOptions) => string
    ): Breadcrumb[] {
      const breadcrumbs: Breadcrumb[] = []

      if (category) {
        breadcrumbs.unshift({ id: category.slug, translation: category.name, display_in_marketplace: 1 })
      }

      let parentId = category?.parent_id

      while (parentId !== null) {
        const rawCategory = allCategories?.find((predicate) => predicate.id === parentId)

        if (!rawCategory) {
          break
        }

        breadcrumbs.unshift({ id: rawCategory.slug, translation: rawCategory.name, display_in_marketplace: 1 })

        parentId = rawCategory.parent_id
      }

      breadcrumbs.unshift({
        id: null,
        translation:
          this.contentType === ContentType.Offers ? t?.('offers.all_offers') : t?.('category.all-categories'),
        display_in_marketplace: 1,
      })

      return breadcrumbs
    },
    setCategoryParam(categoryParam: number | number[] | null) {
      this.setPrevCategoryParam(this.categoryParam)
      this.categoryParam = categoryParam
    },
    setPrevCategoryParam(categoryParam: number | number[] | null) {
      this.prevCategoryParam = categoryParam
    },
    setContentType(type: ContentType) {
      this.contentType = type
    },
  },
  getters: {
    getIsBelowLimit(): boolean {
      return this.getTo < this.total
    },
    getTotalShown(): number {
      return this.products.length
    },
    getTotal(): number {
      let total = SearchEnum.FirstPage
      if (this.page > 1) {
        total = SearchEnum.FirstPage + (this.page - 1) * SearchEnum.PerPage
      }

      return total
    },
    getFrom(): number {
      return this.getTo - this.getTotalShown + 1
    },
    getTo(): number {
      return Math.min(this.getTotal, this.total)
    },
    getFacets: (state) => {
      let result = state.facets.filter(
        (facet) => ![FacetKeyEnum.CATEGORY_ID, FacetKeyEnum.CATEGORY_IDS].includes(facet.key as FacetKeyEnum)
      )

      const isInCategory = !!(state.category && state.category.id > 0)

      if (state.category && isInCategory) {
        const filterableAdditionalFields = state.category.filterable_additional_fields || []
        const filterableAdditionalFieldIds = filterableAdditionalFields.map((predicate) => parseInt(predicate))

        result = result.filter((facet) => {
          const additionalFieldId = extractAdditionalFieldId(facet)

          return additionalFieldId ? filterableAdditionalFieldIds.includes(additionalFieldId) : true
        })
      } else {
        result = result.filter((facet) => {
          const additionalFieldId = extractAdditionalFieldId(facet)

          return additionalFieldId ? !state.disabledAdditionalIdsInIndex.includes(additionalFieldId) : true
        })
      }

      return result
    },
    getAllFacets: (state) => {
      return state.facets
    },
    getCategory(): CategoryItem | CategoryTree | undefined {
      return this.category
    },
    getBreadcrumbs(): Breadcrumb[] {
      return this.categoryBreadcrumbs
    },
    getSelectedFiltersLength(): number | undefined {
      return this.selectedFilters?.size
    },
    getTemporarilySelectedFilters(): Map<string, { value: string[] | number[]; type: FacetType }> | undefined {
      return this.temporarilySelectedFilters
    },
    getSelectedFilters(): Map<string, { value: string[] | number[]; type: FacetType }> | undefined {
      return this.selectedFilters
    },
    getCategoryParam(): number | number[] | null {
      return this.categoryParam
    },
    getPrevCategoryParam(): number | number[] | null {
      return this.prevCategoryParam
    },
    isCategoryChanged(): boolean {
      return JSON.stringify(this.getCategoryParam) !== JSON.stringify(this.getPrevCategoryParam)
    },
    getSimilarResults(): Product[] {
      return this.similarResults
    },
    getContentType(): ContentType {
      return this.contentType
    },
  },
})
